import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import qs from 'query-string';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CircularProgress from '@material-ui/core/CircularProgress';
import { buildLink } from 'Services/tools';
import AwardForm from './Form';
import { fetchCompetitionLink } from '../competitions/actions';
import {
  createTournamentAward,
  deleteTournamentAward,
  fetchTournamentAward,
  updateTournamentAward,
} from './actions';

/**
 * Standalone create/edit page for a tournament award (award id in the URL).
 * - /tournament-awards/new?competition=<id>  (from the competition page: competition fixed)
 * - /tournament-awards/new?winnerTeam=<id>   (from the team page: competition to choose)
 * - /tournament-awards/:awardId              (loads the award and prefills the form)
 *
 * NB: context is passed as a plain id in the query (not an IRI/label, not router state —
 * the app uses HashRouter, which drops location state). The competition label is resolved
 * from the store/API for the read-only display.
 */
class AwardFormPage extends Component {
  state = { award: null, competition: null, loading: false };

  componentDidMount() {
    const { match, dispatch } = this.props;
    const { awardId } = match.params;

    if (awardId) {
      this.setState({ loading: true });
      dispatch(fetchTournamentAward(awardId)).then(award => {
        this.setState({ award, loading: false });
      });
      return;
    }

    // Create from the competition page: resolve the competition for the read-only display.
    const { competition } = this.query;
    if (competition) {
      this.setState({ loading: true });
      dispatch(fetchCompetitionLink(competition)).then(({ payload }) => {
        this.setState({ competition: payload, loading: false });
      });
    }
  }

  get query() {
    return qs.parse(this.props.location.search);
  }

  buildData() {
    const { award } = this.state;
    if (award) {
      return award;
    }

    // Create: prefill the relation coming from the originating view, as an IRI reference.
    const { competition, winnerTeam } = this.query;
    const data = {};
    if (competition) {
      data.competition = { '@id': buildLink('Competition', competition) };
    }
    if (winnerTeam) {
      data.winnerTeam = { '@id': buildLink('Team', winnerTeam) };
    }

    return data;
  }

  competitionOption() {
    const { award, competition } = this.state;
    if (award && award.competition) {
      return { value: award.competition['@id'], label: award.competition.name };
    }
    if (competition) {
      return { value: competition['@id'], label: competition.name };
    }

    return null;
  }

  goBack = () => {
    this.props.history.goBack();
  };

  handleSubmit = values => {
    const { dispatch } = this.props;
    const { award } = this.state;
    const action = award
      ? updateTournamentAward(award.id, values)
      : createTournamentAward(values);

    return dispatch(action).then(this.goBack);
  };

  handleDelete = () => {
    const { dispatch } = this.props;
    const { award } = this.state;

    return dispatch(deleteTournamentAward(award.id)).then(this.goBack);
  };

  render() {
    const { match, intl: { messages: m } } = this.props;
    const { award, loading } = this.state;
    const isEdit = Boolean(match.params.awardId);

    if (loading) {
      return <CircularProgress size={100} style={{ width: 100, margin: 'auto' }} />;
    }
    if (isEdit && !award) {
      return null;
    }

    return (
      <Paper elevation={2} style={{ margin: 16, padding: 16 }}>
        <Button variant="contained" onClick={this.goBack} style={{ marginBottom: 16 }}>
          <ArrowBackIcon style={{ marginRight: 8 }} />
          {m.button_returnList}
        </Button>
        <AwardForm
          key={isEdit ? award.id : 'new'}
          form={isEdit ? `tournamentAward_${award.id}` : 'tournamentAward_create'}
          competitionFixed={Boolean(this.competitionOption())}
          data={this.buildData()}
          competitionOption={this.competitionOption()}
          onSubmit={this.handleSubmit}
          onDelete={isEdit ? this.handleDelete : undefined}
        />
      </Paper>
    );
  }
}

export default connect()(injectIntl(AwardFormPage));
